import { useState, useCallback, useMemo } from "react";
import type { TreeNodeData } from "../TreeViewDataLayer";
import { useAppDispatch } from "../../../store";
import { toggleExpandedNode, setExpandedNodes } from "../../../reducers/treeViewUiState";

interface UseTreeViewNodeStateProps {
  aboData: TreeNodeData | null;
  setAboData: React.Dispatch<React.SetStateAction<TreeNodeData | null>>;
  panToNode: (targetId: string, shouldZoom?: boolean) => void;
  setPendingPanToNodeId: (id: string | null) => void;
  inInternational?: boolean;
  contextKey?: string;
  /** True level (1-indexed) of aboData's root in the full, pre-isolation
   *  tree. Defaults to 1 (the non-isolated case) - pass the isolated ABO's
   *  real level while isolated, since a fresh fetch always reports its own
   *  root as level 1 regardless of where it actually sits in the org. */
  rootLevel?: number;
}

interface UseTreeViewNodeStateReturn {
  selectedAbo: TreeNodeData | null;
  setSelectedAbo: React.Dispatch<React.SetStateAction<TreeNodeData | null>>;
  selectedNodeId: string | null;
  setSelectedNodeId: (id: string | null) => void;
  handleNodeSelect: (abo: TreeNodeData) => void;
  toggleExpand: (targetId: string, recursive?: boolean) => Array<{ aff: number; abo: number; expanded: boolean }>;
  collapseRoot: () => void;
  expandAll: () => Array<{ aff: number; abo: number; expanded: boolean }>;
  collapseToFrontline: () => Array<{ aff: number; abo: number; expanded: boolean }>;
  isExpandedBeyondFrontline: boolean;
  canSelectedAboExpand: boolean;
  canSelectedAboIsolate: boolean;
  isSelectedAboExpanded: boolean;
  selectedAboLevel: number;
  updateNodeWatchlist: (targetAbo: number, targetAff: number, newWatchlistStatus: boolean) => void;
}

/**
 * Hook to manage tree view node selection and expansion state
 */
export const useTreeViewNodeState = ({
  aboData,
  setAboData,
  panToNode,
  setPendingPanToNodeId,
  inInternational = false,
  contextKey = "default",
  rootLevel = 1,
}: UseTreeViewNodeStateProps): UseTreeViewNodeStateReturn => {
  const [selectedAbo, setSelectedAbo] = useState<TreeNodeData | null>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const dispatch = useAppDispatch();

  const handleNodeSelect = useCallback(
    (abo: TreeNodeData) => {
      setSelectedAbo(abo);
      setSelectedNodeId(abo.abo.toString());
      panToNode(abo.abo.toString());
    },
    [panToNode],
  );

  /**
   * Toggle expand state for a node
   * @param targetId - The ABO ID to toggle
   * @param recursive - If true, expands/collapses all descendants. If false (default), only toggles the target node.
   */
  const toggleExpand = useCallback(
    (targetId: string, recursive = false): Array<{ aff: number; abo: number; expanded: boolean }> => {
      const changes: Array<{ key: string; aff: number; abo: number; expanded: boolean }> = [];

      /**
       * Recursively sets expanded state on a node and all its descendants.
       * Stops at international nodes unless inInternational is enabled.
       */
      const setExpandedRecursively = (node: TreeNodeData, expanded: boolean): TreeNodeData => {
        if (node.internationalFlag && !inInternational) {
          return node;
        }
        changes.push({ key: `${node.aff}-${node.abo}`, aff: node.aff, abo: node.abo, expanded });
        return {
          ...node,
          expanded,
          downlines: node.downlines?.map((child) => setExpandedRecursively(child, expanded)),
        };
      };

      const updateNode = (node: TreeNodeData): TreeNodeData => {
        if (node.abo.toString() === targetId) {
          const newExpanded = !node.expanded;
          changes.push({ key: `${node.aff}-${node.abo}`, aff: node.aff, abo: node.abo, expanded: newExpanded });
          if (recursive) {
            // When directly clicking a node, expand it and recurse into children,
            // but stop at any international descendants
            return {
              ...node,
              expanded: newExpanded,
              downlines: node.downlines?.map((child) => setExpandedRecursively(child, newExpanded)),
            };
          }
          // Non-root, non-recursive: directly clicked node always toggles regardless of international flag
          return { ...node, expanded: newExpanded };
        }
        return { ...node, downlines: node.downlines?.map((child) => updateNode(child)) };
      };

      // Compute the updated tree synchronously against current aboData so `changes`
      // is populated before we dispatch — avoids a React batching race where the
      // functional updater runs after dispatch and changes arrive empty.
      const nextData = aboData ? updateNode(aboData) : aboData;
      setAboData(nextData);
      setPendingPanToNodeId(targetId);

      changes.forEach(({ key, expanded }) => {
        dispatch(toggleExpandedNode({ contextKey, nodeId: key, expanded }));
      });

      return changes.map(({ aff, abo, expanded }) => ({ aff, abo, expanded }));
    },
    [setAboData, setPendingPanToNodeId, aboData, inInternational, dispatch, contextKey],
  );

  const updateNodeWatchlist = useCallback(
    (targetAbo: number, targetAff: number, newWatchlistStatus: boolean) => {
      const updateNode = (node: TreeNodeData): TreeNodeData => {
        if (node.abo === targetAbo && node.aff === targetAff) {
          return { ...node, watchlist: newWatchlistStatus };
        }
        return { ...node, downlines: node.downlines?.map(updateNode) };
      };
      setAboData((prev) => (prev ? updateNode(prev) : prev));
    },
    [setAboData],
  );

  /**
   * Collapse the root node so only the root ABO itself is visible, with no downlines shown.
   */
  const collapseRoot = useCallback(() => {
    const collapseRecursively = (node: TreeNodeData): TreeNodeData => ({
      ...node,
      expanded: false,
      downlines: node.downlines?.map((child) => collapseRecursively(child)),
    });

    setAboData((prev) => (prev ? collapseRecursively(prev) : prev));
    dispatch(setExpandedNodes({ contextKey, nodeIds: [] }));
  }, [setAboData, dispatch, contextKey]);

  // True once any frontline child (or deeper descendant) is itself expanded - i.e. the
  // view shows more than just root + first-level downlines. Drives the zoom controls'
  // Expand All / Collapse All button: Collapse All is offered only once this is true.
  const isExpandedBeyondFrontline = useMemo(() => {
    const hasExpandedDescendant = (node: TreeNodeData): boolean =>
      (node.downlines?.some((child) => child.expanded || hasExpandedDescendant(child)) && node.expanded) ?? false;

    return aboData ? hasExpandedDescendant(aboData) : false;
  }, [aboData]);

  /**
   * Expand every fetched node in the tree - equivalent to hitting Expand Group on the
   * root. Stops at international nodes unless inInternational is enabled.
   */
  const expandAll = useCallback((): Array<{ aff: number; abo: number; expanded: boolean }> => {
    const changes: Array<{ key: string; aff: number; abo: number; expanded: boolean }> = [];
    const expandRecursively = (node: TreeNodeData): TreeNodeData => {
      if (node.internationalFlag && !inInternational) {
        return node;
      }
      if (!node.expanded) {
        changes.push({ key: `${node.aff}-${node.abo}`, aff: node.aff, abo: node.abo, expanded: true });
      }
      return {
        ...node,
        expanded: true,
        downlines: node.downlines?.map((child) => expandRecursively(child)),
      };
    };

    // Compute the updated tree synchronously against current aboData so `changes` is
    // populated before we dispatch - avoids a React batching race where the functional
    // updater passed to setAboData runs after this function has already returned
    // (e.g. when called from a modal action handler), leaving `changes` empty and
    // producing no onExpandChange calls for a host to mirror into List View, even
    // though the tree itself did get expanded (ABOT-13129/ABOT-13915 follow-up -
    // matches the fix toggleExpand already applies above).
    const nextData = aboData ? expandRecursively(aboData) : aboData;
    setAboData(nextData);

    changes.forEach(({ key }) => {
      dispatch(toggleExpandedNode({ contextKey, nodeId: key, expanded: true }));
    });

    return changes.map(({ aff, abo, expanded }) => ({ aff, abo, expanded }));
  }, [setAboData, aboData, inInternational, dispatch, contextKey]);

  /**
   * Collapse back to root + first-level downlines (retains frontlines) - the inverse
   * of expandAll and what the zoom controls' Collapse All button applies.
   */
  const collapseToFrontline = useCallback((): Array<{ aff: number; abo: number; expanded: boolean }> => {
    const changes: Array<{ key: string; aff: number; abo: number; expanded: boolean }> = [];

    const collapseRecursively = (node: TreeNodeData): TreeNodeData => {
      if (node.expanded) {
        changes.push({ key: `${node.aff}-${node.abo}`, aff: node.aff, abo: node.abo, expanded: false });
      }
      return {
        ...node,
        expanded: false,
        downlines: node.downlines?.map((child) => collapseRecursively(child)),
      };
    };

    // Same synchronous-first rationale as expandAll above - compute against the
    // current aboData directly rather than inside setAboData's functional updater.
    const nextData = aboData
      ? {
          ...aboData,
          expanded: true,
          downlines: aboData.downlines?.map((child) => collapseRecursively(child)),
        }
      : aboData;
    setAboData(nextData);

    changes.forEach(({ key }) => {
      dispatch(toggleExpandedNode({ contextKey, nodeId: key, expanded: false }));
    });

    return changes.map(({ aff, abo, expanded }) => ({ aff, abo, expanded }));
  }, [setAboData, aboData, dispatch, contextKey]);

  const canSelectedAboExpand = useMemo(() => Boolean(selectedAbo?.downlines?.length), [selectedAbo]);

  // Downline LOS is available only when the selected ABO has direct downlines
  // loaded in the current tree, matching the Tree Map's visible hierarchy.
  const canSelectedAboIsolate = useMemo(() => Boolean(selectedAbo?.downlines?.length), [selectedAbo]);

  const isSelectedAboExpanded = useMemo(() => {
    if (!selectedAbo || !aboData) return false;

    // Root's own `expanded` field is force-kept true in several places upstream (the
    // canvas layout hides all children the instant it's false) so it can't reliably
    // signal root's Expand/Collapse Group state - use the same tree-wide "expanded
    // beyond frontline" signal the toolbar's Expand All/Collapse All button uses instead,
    // so the modal and toolbar always agree.
    if (selectedAbo.abo === aboData.abo) {
      return isExpandedBeyondFrontline;
    }

    const findNode = (node: TreeNodeData): TreeNodeData | null => {
      if (node.abo === selectedAbo.abo) return node;
      for (const child of node.downlines ?? []) {
        const found = findNode(child);
        if (found) return found;
      }
      return null;
    };

    return findNode(aboData)?.expanded ?? false;
  }, [selectedAbo, aboData, isExpandedBeyondFrontline]);

  const selectedAboLevel = useMemo(() => {
    if (!selectedAbo || !aboData) return rootLevel;

    const findLevel = (node: TreeNodeData, currentLevel: number): number | null => {
      if (node.abo === selectedAbo.abo) return currentLevel;
      for (const child of node.downlines ?? []) {
        const level = findLevel(child, currentLevel + 1);
        if (level !== null) return level;
      }
      return null;
    };

    return findLevel(aboData, rootLevel) ?? rootLevel;
  }, [selectedAbo, aboData, rootLevel]);

  return {
    selectedAbo,
    setSelectedAbo,
    selectedNodeId,
    setSelectedNodeId,
    handleNodeSelect,
    toggleExpand,
    collapseRoot,
    expandAll,
    collapseToFrontline,
    isExpandedBeyondFrontline,
    canSelectedAboExpand,
    canSelectedAboIsolate,
    isSelectedAboExpanded,
    selectedAboLevel,
    updateNodeWatchlist,
  };
};
